import type { WidgetId } from './types/panelItem';

// --- Widget identity ---

/** Optional agent-authored `id` on a component's params. */
export const readAuthoredWidgetId = (params: unknown): WidgetId | undefined => {
  if (typeof params !== 'object' || params === null || !('id' in params)) {
    return undefined;
  }
  const id = (params as { id?: unknown }).id;
  return typeof id === 'string' && id.length > 0 ? (id as WidgetId) : undefined;
};

/** Deterministic JSON with sorted object keys, so equal content yields an equal string. */
const stableStringify = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (value !== null && typeof value === 'object') {
    const entries = Object.keys(value as Record<string, unknown>)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify((value as Record<string, unknown>)[key])}`);
    return `{${entries.join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
};

/** djb2 hash → base36; small, dependency-free, good enough for widget identity. */
const hash = (input: string): string => {
  let h = 5381;
  for (let i = 0; i < input.length; i += 1) {
    h = (h * 33) ^ input.charCodeAt(i);
  }
  return (h >>> 0).toString(36);
};

export const widgetId = (source: { messageId: string; componentName: string; params: unknown }): WidgetId =>
  readAuthoredWidgetId(source.params) ??
  (`w_${hash(`${source.messageId} ${source.componentName} ${stableStringify(source.params)}`)}` as WidgetId);
